import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { NgClass, NgIf, NgFor } from '@angular/common';
import { DataService, Card } from '../../../services/data.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { FormsModule } from '@angular/forms';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FontSizeService, FontSizeOption } from '../../../services/font-size.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-details-area',
  standalone: true,
  imports: [
    MatToolbar,
    MatIcon,
    MatDivider,
    NgClass,
    NgIf,
    NgFor,
    MatProgressSpinnerModule,
    MatCardModule,
    MatCheckboxModule,
    MatTabsModule,
    FormsModule,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatTooltip,
    MatIconButton,
    MatSlideToggleModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './details-area.component.html',
  styleUrl: './details-area.component.css'
})
export class DetailsAreaComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedCard: Card | null = null;
  @Input() isFullscreenMode = false;
  @Input() isMobileView = false;

  // Cached option image arrays — recomputed only when selectedCard changes
  optionImages: { label: string; url: string }[] = [];
  hindiOptionImages: { label: string; url: string }[] = [];

  // Input for showMains from parent component
  @Input() showMains = true;
  @Output() showMainsChange = new EventEmitter<boolean>();
  @Output() backToMain = new EventEmitter<void>();
  @Output() closeDetails = new EventEmitter<void>();
  @Output() viewModeChange = new EventEmitter<string>();

  // View mode property - updated to include popup
  viewMode: 'split' | 'fullscreen' | 'popup' = 'split';

  // View menu layout direction
  viewMenuLayoutDirection: 'vertical' | 'horizontal' = 'vertical';

  // Show question flag
  showQuestion = false;

  // Show Hindi flag
  showHindi = false;

  // Selected tab index
  selectedTabIndex = 0;
  ocrTabIndex = 0;

  // Track if images are loaded
  answerImageLoaded = false;
  explanationImageLoaded = false;
  questionImageLoaded = false;

  // Track if images have errors
  answerImageError = false;
  explanationImageError = false;
  questionImageError = false;

  // Track Hindi image loading
  hindiAnswerImageLoaded = false;
  hindiExplanationImageLoaded = false;
  hindiQuestionImageLoaded = false;

  // Track Hindi image errors
  hindiAnswerImageError = false;
  hindiExplanationImageError = false;
  hindiQuestionImageError = false;

  // Add destroy Subject for subscription management
  private destroy$ = new Subject<void>();

  // Add currentFontSize property
  currentFontSize: FontSizeOption = 'medium';

  constructor(
    private dataService: DataService,
    private fontSizeService: FontSizeService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Initialize component

    // Subscribe to font size changes
    this.fontSizeService.fontSize$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(fontSize => {
      this.currentFontSize = fontSize;
      // Force refresh if needed
      this.cdr.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCard']) {
      // Reset image states when selected card changes
      this.resetImageStates();

      // Recompute cached option image arrays
      this._computeOptionImages();

      // Log the selected card for debugging
      if (this.selectedCard) {
        console.log('Details area received new card:', this.selectedCard.id);

        // When a new card is selected, ensure this component is visible
        // and scrolls into view if needed
        setTimeout(() => {
          // Force component to update
          this.cdr.detectChanges();

          // Try to make this component element visible in the viewport
          const detailsElement = document.querySelector('app-details-area');
          if (detailsElement) {
            console.log('Details area ensuring visibility');
            detailsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        console.log('Details area: No card selected');
      }
    }

    // React to fullscreen mode changes
    if (changes['isFullscreenMode'] && changes['isFullscreenMode'].currentValue === true) {
      // If entering fullscreen mode, initialize showMains based on parent value
      console.log('Details area entering fullscreen mode, showMains:', this.showMains);
    }
  }

  // Handle Show Mains checkbox change
  onShowMainsChange(): void {
    console.log('Show mains changed to:', this.showMains);

    // If Show Mains is checked, automatically change to side-pane view mode
    if (this.showMains) {
      this.viewMode = 'split';
      this.viewModeChange.emit('split');
      console.log('Changing to side-pane mode because Show Mains was checked');
    }

    this.showMainsChange.emit(this.showMains);
  }

  resetImageStates(): void {
    this.answerImageLoaded = false;
    this.explanationImageLoaded = false;
    this.questionImageLoaded = false;
    this.answerImageError = false;
    this.explanationImageError = false;
    this.questionImageError = false;

    // Reset Hindi image states
    this.hindiAnswerImageLoaded = false;
    this.hindiExplanationImageLoaded = false;
    this.hindiQuestionImageLoaded = false;
    this.hindiAnswerImageError = false;
    this.hindiExplanationImageError = false;
    this.hindiQuestionImageError = false;
  }

  onAnswerImageLoad(): void {
    this.answerImageLoaded = true;
    this.answerImageError = false;
    console.log('Answer image loaded successfully');
  }

  onAnswerImageError(): void {
    this.answerImageLoaded = true; // Still mark as "loaded" to remove spinner
    this.answerImageError = true;
    console.warn('Error loading answer image');
  }

  onExplanationImageLoad(): void {
    this.explanationImageLoaded = true;
    this.explanationImageError = false;
    console.log('Explanation image loaded successfully');
  }

  onExplanationImageError(): void {
    this.explanationImageLoaded = true; // Still mark as "loaded" to remove spinner
    this.explanationImageError = true;
    console.warn('Error loading explanation image');
  }

  onQuestionImageLoad(): void {
    this.questionImageLoaded = true;
    this.questionImageError = false;
    console.log('Question image loaded successfully');
  }

  onQuestionImageError(): void {
    this.questionImageLoaded = true; // Still mark as "loaded" to remove spinner
    this.questionImageError = true;
    console.warn('Error loading question image');
  }

  onHindiAnswerImageLoad(): void {
    this.hindiAnswerImageLoaded = true;
    this.hindiAnswerImageError = false;
    console.log('Hindi answer image loaded successfully');
  }

  onHindiAnswerImageError(): void {
    this.hindiAnswerImageLoaded = true;
    this.hindiAnswerImageError = true;
    console.warn('Error loading Hindi answer image');
  }

  onHindiExplanationImageLoad(): void {
    this.hindiExplanationImageLoaded = true;
    this.hindiExplanationImageError = false;
    console.log('Hindi explanation image loaded successfully');
  }

  onHindiExplanationImageError(): void {
    this.hindiExplanationImageLoaded = true;
    this.hindiExplanationImageError = true;
    console.warn('Error loading Hindi explanation image');
  }

  onHindiQuestionImageLoad(): void {
    this.hindiQuestionImageLoaded = true;
    this.hindiQuestionImageError = false;
    console.log('Hindi question image loaded successfully');
  }

  onHindiQuestionImageError(): void {
    this.hindiQuestionImageLoaded = true;
    this.hindiQuestionImageError = true;
    console.warn('Error loading Hindi question image');
  }

  getCorrectAnswerLetter(): string {
    if (!this.selectedCard) return '';
    const answerText = this.selectedCard.answerText || '';
    // Handle OCR format: "(a) Option text"
    const matchParen = answerText.match(/\(([a-dA-D])\)/);
    if (matchParen) return matchParen[1].toUpperCase();
    // Handle standard format: "A. Option text"
    const matchDot = answerText.match(/^([A-D])[.\s]/i);
    if (matchDot) return matchDot[1].toUpperCase();
    return '';
  }

  onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
    console.log('Tab changed to:', this.selectedTabIndex);
  }

  goBackToMain(): void {
    console.log('Going back to main area');
    this.backToMain.emit();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getViewModeIcon(): string {
    switch (this.viewMode) {
      case 'split':
        return 'view_column';
      case 'popup':
        return 'open_in_new';
      case 'fullscreen':
        return 'fullscreen';
      default:
        return 'view_quilt';
    }
  }

  setViewMode(mode: 'split' | 'fullscreen' | 'popup'): void {
    console.log('Setting view mode to:', mode);
    this.viewMode = mode;
    this.viewModeChange.emit(mode);

    if (mode === 'fullscreen') {
      // When entering fullscreen mode, automatically uncheck Show Mains
      this.showMains = false;
      this.showMainsChange.emit(false);
      console.log('Entering fullscreen mode, showMains set to false');
    } else if (mode === 'popup') {
      // Additional logic if needed for popup mode
      console.log('Entering popup mode');
    } else {
      // Additional logic if needed for split mode
      console.log('Entering split mode');
    }
  }

  closeDetailsArea(): void {
    console.log('Closing details area');
    this.closeDetails.emit();
  }

  /**
   * Recomputes the cached option image arrays from the current selectedCard.
   * Called once per card change to avoid re-fetching on every change detection.
   */
  private _computeOptionImages(): void {
    if (!this.selectedCard) {
      this.optionImages = [];
      this.hindiOptionImages = [];
      return;
    }
    const c = this.selectedCard;
    this.optionImages = [
      { key: 'A', eng: c.optionAImageUrl, hin: c.hindiOptionAImageUrl },
      { key: 'B', eng: c.optionBImageUrl, hin: c.hindiOptionBImageUrl },
      { key: 'C', eng: c.optionCImageUrl, hin: c.hindiOptionCImageUrl },
      { key: 'D', eng: c.optionDImageUrl, hin: c.hindiOptionDImageUrl },
    ]
      .filter(o => o.eng || o.hin)
      .map(o => ({ label: o.key, url: (o.eng || o.hin)! }));

    this.hindiOptionImages = [
      { key: 'A', url: c.hindiOptionAImageUrl },
      { key: 'B', url: c.hindiOptionBImageUrl },
      { key: 'C', url: c.hindiOptionCImageUrl },
      { key: 'D', url: c.hindiOptionDImageUrl },
    ]
      .filter(o => !!o.url)
      .map(o => ({ label: o.key, url: o.url! }));
  }

  /**
   * Returns an array of option images that have valid URLs.
   * Falls back to Hindi option URLs if English ones are empty (bilingual mode).
   * @deprecated Use the cached `optionImages` property in templates instead.
   */
  getOptionImages(): { label: string; url: string }[] {
    if (!this.selectedCard) return [];
    const options: { label: string; url: string }[] = [];
    const c = this.selectedCard;
    if (c.optionAImageUrl || c.hindiOptionAImageUrl) options.push({ label: 'A', url: c.optionAImageUrl || c.hindiOptionAImageUrl! });
    if (c.optionBImageUrl || c.hindiOptionBImageUrl) options.push({ label: 'B', url: c.optionBImageUrl || c.hindiOptionBImageUrl! });
    if (c.optionCImageUrl || c.hindiOptionCImageUrl) options.push({ label: 'C', url: c.optionCImageUrl || c.hindiOptionCImageUrl! });
    if (c.optionDImageUrl || c.hindiOptionDImageUrl) options.push({ label: 'D', url: c.optionDImageUrl || c.hindiOptionDImageUrl! });
    return options;
  }

  /**
   * Returns an array of Hindi option images that have valid URLs.
   */
  getHindiOptionImages(): { label: string; url: string }[] {
    if (!this.selectedCard) return [];
    const options: { label: string; url: string }[] = [];
    const c = this.selectedCard;
    if (c.hindiOptionAImageUrl) options.push({ label: 'A', url: c.hindiOptionAImageUrl });
    if (c.hindiOptionBImageUrl) options.push({ label: 'B', url: c.hindiOptionBImageUrl });
    if (c.hindiOptionCImageUrl) options.push({ label: 'C', url: c.hindiOptionCImageUrl });
    if (c.hindiOptionDImageUrl) options.push({ label: 'D', url: c.hindiOptionDImageUrl });
    return options;
  }

  // Check if currently in popup mode
  isInPopupMode(): boolean {
    return this.viewMode === 'popup';
  }

  // Toggle view menu layout between vertical and horizontal
  toggleViewMenuLayout(event: any): void {
    this.viewMenuLayoutDirection = event.checked ? 'horizontal' : 'vertical';
    console.log('View menu layout direction changed to:', this.viewMenuLayoutDirection);
  }
}
